import { useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import { StatusBadge, PassTypeBadge, formatDate } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { getVPPending, vpAction } from "../lib/api";
import { Clock, CheckCircle2, Eye, Loader2, UserCog } from "lucide-react";
import TodayPastPasses from "../components/TodayPastPasses";

const VicePrincipalDashboard = () => {
  return (
    <DashboardLayout title="Vice Principal Portal">
      <Routes>
        <Route index element={<VPHome />} />
      </Routes>
    </DashboardLayout>
  );
};

const VPHome = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPass, setSelectedPass] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data } = await getVPPending();
      setPending(data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (actionType === "reject" && !rejectionReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }

    setSubmitting(true);
    try {
      await vpAction(selectedPass.id, {
        action: actionType,
        remarks: remarks.trim() || null,
        rejection_reason: actionType === "reject" ? rejectionReason : null
      });
      toast.success(actionType === "approve" ? "Request approved!" : "Request rejected");
      setSelectedPass(null);
      setActionType(null);
      setRemarks("");
      setRejectionReason("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  const getActingAs = (status) => {
    if (status === "pending_vp_as_coordinator") return "Coordinator";
    if (status === "pending_vp_as_principal") return "Principal";
    return "Unknown";
  };

  return (
    <div className="space-y-6" data-testid="vp-dashboard">
      {/* Info Banner */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
        <UserCog className="h-6 w-6 text-amber-600" />
        <div>
          <p className="font-medium text-amber-800">Acting on behalf of unavailable staff</p>
          <p className="text-sm text-amber-600">You are approving requests when Coordinator or Principal is unavailable.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending (as Coordinator)</p>
                <p className="text-2xl font-semibold text-amber-600">
                  {pending.filter(p => p.status === "pending_vp_as_coordinator").length}
                </p>
              </div>
              <Clock className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending (as Principal)</p>
                <p className="text-2xl font-semibold text-blue-600">
                  {pending.filter(p => p.status === "pending_vp_as_principal").length}
                </p>
              </div>
              <Clock className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Requests */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Delegated Requests</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : pending.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-300" />
              <p>No delegated requests!</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Request No.</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Acting As</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Exit Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((req) => (
                  <TableRow key={req.id} data-testid={`pending-row-${req.id}`}>
                    <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{req.student_name}</p>
                        <p className="text-xs text-slate-500">{req.course_name} - Y{req.year}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-slate-100">
                        Acting as {getActingAs(req.status)}
                      </Badge>
                    </TableCell>
                    <TableCell><PassTypeBadge type={req.pass_type} /></TableCell>
                    <TableCell>{formatDate(req.exit_date)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setSelectedPass(req); setActionType(null); }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => { setSelectedPass(req); setActionType("approve"); }}
                          data-testid={`approve-btn-${req.id}`}
                        >
                          Approve
                        </Button>
                        <Button 
                          size="sm"
                          variant="destructive"
                          onClick={() => { setSelectedPass(req); setActionType("reject"); }}
                          data-testid={`reject-btn-${req.id}`}
                        >
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={!!selectedPass && !!actionType} onOpenChange={() => { setSelectedPass(null); setActionType(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {actionType === "approve" ? "Approve Request" : "Reject Request"}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPass && (
            <div className="space-y-4">
              <Badge variant="outline" className="bg-amber-50 text-amber-700">
                Acting as {getActingAs(selectedPass.status)}
              </Badge>
              
              <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                <p><span className="text-slate-500">Request:</span> {selectedPass.request_number}</p>
                <p><span className="text-slate-500">Student:</span> {selectedPass.student_name}</p>
                <p><span className="text-slate-500">Reason:</span> {selectedPass.reason}</p>
                <p><span className="text-slate-500">Exit:</span> {formatDate(selectedPass.exit_date)} at {selectedPass.exit_time}</p>
              </div>

              {actionType === "reject" && (
                <div className="space-y-2">
                  <Label className="text-red-600">Rejection Reason *</Label>
                  <Textarea
                    placeholder="Enter the reason for rejection (required)"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="border-red-200"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Remarks (Optional)</Label>
                <Textarea
                  placeholder="Add any remarks"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedPass(null); setActionType(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAction}
              disabled={submitting}
              className={actionType === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              variant={actionType === "reject" ? "destructive" : "default"}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : actionType === "approve" ? "Confirm Approval" : "Confirm Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!selectedPass && !actionType} onOpenChange={() => setSelectedPass(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Gate Pass Details</DialogTitle>
          </DialogHeader>
          {selectedPass && (
            <div className="space-y-4 text-sm">
              <Badge variant="outline" className="bg-amber-50 text-amber-700">
                Acting as {getActingAs(selectedPass.status)}
              </Badge>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-500">Request Number</p>
                  <p className="font-mono">{selectedPass.request_number}</p>
                </div>
                <div>
                  <p className="text-slate-500">Student</p>
                  <p className="font-medium">{selectedPass.student_name}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Reason</p>
                  <p>{selectedPass.reason}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Destination</p>
                  <p>{selectedPass.destination}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Gate Pass Activity - at bottom */}
      <TodayPastPasses showPrint={true} />
    </div>
  );
};

export default VicePrincipalDashboard;
